import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { api } from '../api/client';
import { useSession } from '../app/session';
import { cleanValues, EntityForm, type FieldSpec, useAssetUrls } from '../components/EntityForm';
import { Empty, ErrorBox, Field, Loading, PageHeader } from '../components/ui';
import { Pager, usePaged } from '../components/Pager';
import { ask } from '../components/Confirm';

type Kind = 'vod' | 'aod';
export interface Category {
  id: string;
  key: string;
  name: string;
  imageAssetId: string | null;
  sortOrder: number;
}
interface Item {
  id: string;
  title: string;
  enabled: boolean;
  categories: { categoryId: string }[];
  [k: string]: unknown;
}

const slug = (s: string) =>
  s
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

function CategoryPicker({ categories, value, onChange }: { categories: Category[]; value: string[]; onChange: (v: string[]) => void }) {
  return (
    <Field label="Categories" hint="Packages unlock content by category (Content › Packages).">
      <div className="row">
        {categories.map((c) => {
          const on = value.includes(c.id);
          return (
            <button key={c.id} className={`btn small${on ? ' primary' : ''}`} onClick={() => onChange(on ? value.filter((x) => x !== c.id) : [...value, c.id])}>
              {c.name}
            </button>
          );
        })}
        {categories.length === 0 && <span className="muted">Create categories first (Categories tab).</span>}
      </div>
    </Field>
  );
}

const vodFields = (cats: Category[]): FieldSpec[] => [
  { key: 'title', label: 'Title', type: 'text', required: true },
  { key: 'sourceUrl', label: 'Video URL (MP4, H.264/AAC)', type: 'url', required: true, hint: 'From the hotel server, NAS or Edge cache: e.g. http://10.0.0.5/media/vod/film.mp4' },
  { key: 'description', label: 'Synopsis', type: 'textarea' },
  { key: 'posterAssetId', label: 'Poster (portrait)', type: 'image', urlKey: 'posterUrl' },
  { key: 'backdropAssetId', label: 'Backdrop (16:9)', type: 'image', urlKey: 'backdropUrl' },
  { key: 'year', label: 'Year', type: 'number' },
  { key: 'durationMin', label: 'Duration (minutes)', type: 'number' },
  { key: 'rating', label: 'Rating', type: 'text', placeholder: 'PG-13' },
  { key: 'genres', label: 'Genres', type: 'tags', placeholder: 'Action, Drama' },
  { key: 'audioLanguages', label: 'Audio languages', type: 'tags', placeholder: 'English, Arabic' },
  { key: 'subtitleLanguages', label: 'Subtitles', type: 'tags' },
  {
    key: 'categoryIds',
    label: 'Categories',
    type: 'custom',
    render: (v, set) => <CategoryPicker categories={cats} value={(v.categoryIds as string[]) ?? []} onChange={(categoryIds) => set({ categoryIds })} />,
  },
  { key: 'featured', label: 'Featured (hero carousel)', type: 'toggle' },
  { key: 'enabled', label: 'Shown on TV', type: 'toggle' },
];

const aodFields = (cats: Category[]): FieldSpec[] => [
  { key: 'title', label: 'Title', type: 'text', required: true },
  { key: 'artist', label: 'Artist', type: 'text' },
  { key: 'album', label: 'Album', type: 'text' },
  { key: 'sourceUrl', label: 'Audio URL (AAC / MP3)', type: 'url', required: true },
  { key: 'durationSec', label: 'Duration (seconds)', type: 'number' },
  { key: 'coverAssetId', label: 'Cover', type: 'image', urlKey: 'coverUrl' },
  {
    key: 'categoryIds',
    label: 'Categories',
    type: 'custom',
    render: (v, set) => <CategoryPicker categories={cats} value={(v.categoryIds as string[]) ?? []} onChange={(categoryIds) => set({ categoryIds })} />,
  },
  { key: 'enabled', label: 'Shown on TV', type: 'toggle' },
];

export function MediaLibraryPage({ kind }: { kind: Kind }) {
  const [tab, setTab] = useState<'items' | 'categories'>('items');
  const isVod = kind === 'vod';
  return (
    <>
      <PageHeader
        title={isVod ? 'VOD – Movies' : 'AOD – Music'}
        sub={
          isVod
            ? 'Movies played from the hotel server, NAS or Edge (MP4 H.264/AAC). Shared by all sites; packages decide who sees what.'
            : 'Music for the rooms (AAC / MP3). Shared by all sites; packages decide who hears what.'
        }
      />
      <div className="tabs">
        <button className={`tab${tab === 'items' ? ' active' : ''}`} onClick={() => setTab('items')}>
          {isVod ? 'Movies' : 'Tracks'}
        </button>
        <button className={`tab${tab === 'categories' ? ' active' : ''}`} onClick={() => setTab('categories')}>
          Categories
        </button>
      </div>
      {tab === 'items' ? <Items kind={kind} /> : <Categories kind={kind} />}
    </>
  );
}

export function useCategories(kind: Kind) {
  return useQuery({ queryKey: [kind, 'categories'], queryFn: () => api.get<{ items: Category[] }>(`/${kind}/categories`) });
}

function Items({ kind }: { kind: Kind }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const url = useAssetUrls();
  const cats = useCategories(kind);
  const path = kind === 'vod' ? '/vod/titles' : '/aod/tracks';
  const items = useQuery({ queryKey: [kind, 'items'], queryFn: () => api.get<{ items: Item[] }>(path) });
  const [edit, setEdit] = useState<Item | 'new' | null>(null);
  const remove = useMutation({ mutationFn: (id: string) => api.delete(`${path}/${id}`), onSuccess: () => qc.invalidateQueries({ queryKey: [kind, 'items'] }) });
  const fields = (kind === 'vod' ? vodFields : aodFields)(cats.data?.items ?? []);
  const catName = (id: string) => cats.data?.items.find((c) => c.id === id)?.name;
  const list = items.data?.items ?? [];
  const { rows: paged, pager } = usePaged(list);
  if (items.isLoading) return <Loading />;

  return (
    <div className="card">
      <div className="row" style={{ marginBottom: 12 }}>
        <span className="muted">{list.length} items</span>
        {can('ADMIN') && (
          <button className="btn primary" style={{ marginLeft: 'auto' }} onClick={() => setEdit('new')}>
            <Plus size={15} /> Add
          </button>
        )}
      </div>
      {list.length === 0 ? (
        <Empty>Nothing in the library yet.</Empty>
      ) : (
      <>
        <table>
          <thead>
            <tr>
              <th />
              <th>Title</th>
              <th>Categories</th>
              <th>Source</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {paged.map((it) => {
              const img = url(kind === 'vod' ? it.posterAssetId : it.coverAssetId);
              return (
                <tr key={it.id} style={!it.enabled ? { opacity: 0.5 } : undefined}>
                  <td style={{ width: 54 }}>
                    {img ? <img src={img} alt="" style={{ width: 40, height: kind === 'vod' ? 58 : 40, objectFit: 'cover', borderRadius: 4 }} /> : <span className="logo-placeholder" style={{ width: 40 }} />}
                  </td>
                  <td>
                    <strong>{it.title}</strong>
                    <div className="muted">
                      {kind === 'vod' ? [it.year, it.durationMin ? `${it.durationMin} min` : null, it.rating, it.featured ? '★ featured' : null].filter(Boolean).join(' · ') : [it.artist, it.album].filter(Boolean).join(' · ')}
                    </div>
                  </td>
                  <td>
                    {it.categories.map((c) => (
                      <span key={c.categoryId} className="badge" style={{ marginRight: 4 }}>
                        {catName(c.categoryId)}
                      </span>
                    ))}
                  </td>
                  <td className="mono" style={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {it.sourceUrl as string}
                  </td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {can('ADMIN') && (
                      <>
                        <button className="icon-btn" onClick={() => setEdit(it)}>
                          <Pencil size={15} />
                        </button>
                        <button className="icon-btn" onClick={() => ask(`Delete ${it.title}?`, () => remove.mutate(it.id))}>
                          <Trash2 size={15} />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <Pager {...pager} />
      </>
      )}
      {edit && (
        <EntityForm
          title={edit === 'new' ? 'Add' : `Edit ${edit.title}`}
          fields={fields}
          wide
          initial={
            edit === 'new'
              ? { enabled: true, featured: false, genres: [], audioLanguages: [], subtitleLanguages: [], categoryIds: [] }
              : {
                  ...edit,
                  categoryIds: edit.categories.map((c) => c.categoryId),
                  posterUrl: url(edit.posterAssetId),
                  backdropUrl: url(edit.backdropAssetId),
                  coverUrl: url(edit.coverAssetId),
                }
          }
          onClose={() => setEdit(null)}
          onSave={async (v) => {
            const body = cleanValues(v, fields);
            if (edit === 'new') await api.post(path, body);
            else await api.put(`${path}/${edit.id}`, body);
            await qc.invalidateQueries({ queryKey: [kind, 'items'] });
          }}
        />
      )}
    </div>
  );
}

export function Categories({ kind }: { kind: Kind }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const url = useAssetUrls();
  const cats = useCategories(kind);
  const [edit, setEdit] = useState<Category | 'new' | null>(null);
  const invalidate = () => qc.invalidateQueries({ queryKey: [kind, 'categories'] });
  const remove = useMutation({ mutationFn: (id: string) => api.delete(`/${kind}/categories/${id}`), onSuccess: invalidate });
  const fields: FieldSpec[] = [
    { key: 'name', label: 'Name', type: 'text', required: true, placeholder: kind === 'vod' ? 'Action' : 'Relaxation' },
    { key: 'imageAssetId', label: 'Tile image', type: 'image', urlKey: 'imageUrl', hint: 'Shown on the "Browse by category" tiles.' },
    { key: 'sortOrder', label: 'Order', type: 'number' },
  ];
  if (cats.isLoading) return <Loading />;
  return (
    <div className="card" style={{ maxWidth: 720 }}>
      <div className="row" style={{ marginBottom: 12 }}>
        <span className="muted">Packages unlock content per category.</span>
        {can('ADMIN') && (
          <button className="btn primary" style={{ marginLeft: 'auto' }} onClick={() => setEdit('new')}>
            <Plus size={15} /> Add category
          </button>
        )}
      </div>
      <table>
        <tbody>
          {(cats.data?.items ?? []).map((c) => (
            <tr key={c.id}>
              <td style={{ width: 80 }}>
                {url(c.imageAssetId) ? <img src={url(c.imageAssetId)!} alt="" style={{ width: 64, height: 36, objectFit: 'cover', borderRadius: 4 }} /> : null}
              </td>
              <td>
                <strong>{c.name}</strong> <span className="muted mono">{c.key}</span>
              </td>
              <td style={{ textAlign: 'right' }}>
                {can('ADMIN') && (
                  <>
                    <button className="icon-btn" onClick={() => setEdit(c)}>
                      <Pencil size={15} />
                    </button>
                    <button className="icon-btn" onClick={() => ask(`Delete ${c.name}?`, () => remove.mutate(c.id))}>
                      <Trash2 size={15} />
                    </button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <ErrorBox error={remove.error} />
      {edit && (
        <EntityForm
          title={edit === 'new' ? 'Add category' : `Edit ${edit.name}`}
          fields={fields}
          initial={edit === 'new' ? {} : { ...edit, imageUrl: url(edit.imageAssetId) }}
          onClose={() => setEdit(null)}
          onSave={async (v) => {
            const body = cleanValues(v, fields);
            if (body.sortOrder === null) delete body.sortOrder;
            if (edit === 'new') await api.post(`/${kind}/categories`, { ...body, key: slug(String(body.name)) });
            else await api.patch(`/${kind}/categories/${edit.id}`, body);
            await invalidate();
          }}
        />
      )}
    </div>
  );
}
